import React, { useEffect, useState } from 'react';
import { bp_mentions } from '../../../services';
import CommonModal from '../../../Components/CommonModal';
import CommonLoader from '../../../Components/CommonLoader';
import CommonPagination from '../../../Components/CommonPagination';
import CommonButton from '../../../Components/CommonButton';
import { Body } from '../../../Constants/Fonts';
import { formatNumber, timeAgo, unwrap, PLATFORM_ICON } from './_shared';
import { profileUrlFor } from './_avatarKit';
import { platformLabel } from './LanguageAnalysis';

const PAGE_SIZE = 10;

const postUrlOf = m => m.url || profileUrlFor(m.handle, m.platform) || null;

const postsQuery = (target, days) => {
    if (target.kind === 'author') {
        const q = { days, author: target.handle, search: target.handle };
        if (target.platform) q.platform = String(target.platform).toLowerCase();
        return q;
    }
    return { days, platform: String(target.platform || '').toLowerCase() };
};

// The tab is opened synchronously inside the click so the pop-up blocker allows
// it, then pointed at the post once its URL is known.
export function openPostsFor(target, days, count, showList) {
    showList(target);
}

export default function PostListModal({ target, days, onViewInMentions, onClose }) {
    const [page, setPage] = useState(1);
    const [state, setState] = useState({ loading: true, posts: [], pages: 1, total: 0 });

    useEffect(() => {
        let alive = true;
        setState(s => ({ ...s, loading: true }));
        bp_mentions({ ...postsQuery(target, days), page, page_size: PAGE_SIZE, sort: '-posted_at' })
            .then(r => {
                if (!alive) return;
                const d = unwrap(r) || {};
                setState({ loading: false, posts: d.results || [], pages: d.pages || 1, total: d.total || 0 });
            })
            .catch(() => { if (alive) setState({ loading: false, posts: [], pages: 1, total: 0 }); });
        return () => { alive = false; };
    }, [target, days, page]);

    const isAuthor = target.kind === 'author';
    const where = platformLabel(target.platform);
    const title = isAuthor
        ? `Posts by @${target.handle}${target.platform ? ` on ${where}` : ''}`
        : `Posts from ${where}`;

    return (
        <CommonModal
            show={true}
            onHide={onClose}
            size="lg"
            title={title}
            subtitle={`${formatNumber(state.total)} post${state.total === 1 ? '' : 's'} in the last ${days} days, newest first. Each opens the original post on ${where} in a new tab.`}
        >
            <div className="bm_postlist">
                {state.loading && <CommonLoader label="Loading posts…" />}
                {!state.loading && state.posts.length === 0 && (
                    <Body variant={2} className="bm_postlist_empty">No posts found for this period.</Body>
                )}
                {!state.loading && state.posts.map(m => {
                    const url = postUrlOf(m);
                    const p = String(m.platform || '').toLowerCase();
                    const isProfileOnly = !m.url && !!url;
                    const body = (
                        <>
                            <span className="bm_postlist_platform" title={platformLabel(p)} aria-hidden="true">
                                {PLATFORM_ICON[p] || platformLabel(p).charAt(0)}
                            </span>
                            <span className="bm_postlist_main">
                                <span className="bm_postlist_meta">
                                    {!isAuthor && (
                                        <Body as="span" variant={2} weight="semibold" className="bm_postlist_handle">{m.handle || '(no handle)'}</Body>
                                    )}
                                    <Body as="span" variant={2} className="bm_postlist_time">{timeAgo(m.posted_at)}</Body>
                                    <Body as="span" variant={2} className="bm_postlist_eng" title="Likes + comments + shares">
                                        {formatNumber(Number(m.engagement) || 0)} engagement
                                    </Body>
                                </span>
                                <Body variant={2} className="bm_postlist_text">{m.text}</Body>
                            </span>
                            <Body as="span" variant={2} weight="semibold" className="bm_postlist_open">
                                {url ? (isProfileOnly ? 'Open profile ↗' : 'Open post ↗') : 'No link'}
                            </Body>
                        </>
                    );
                    return url ? (
                        <a key={m.id} className="bm_postlist_row" href={url} target="_blank" rel="noopener noreferrer"
                            title={isProfileOnly
                                ? 'The source gave no post link, so this opens the author\'s profile'
                                : `Open this post on ${platformLabel(p)}`}>
                            {body}
                        </a>
                    ) : (
                        <div key={m.id} className="bm_postlist_row is-disabled">{body}</div>
                    );
                })}
                {!state.loading && state.pages > 1 && (
                    <CommonPagination
                        pageCount={state.pages}
                        forcePage={page - 1}
                        handlePageClick={({ selected }) => setPage(selected + 1)}
                    />
                )}
                <div className="bm_postlist_footer">
                    <CommonButton text="View all in Mentions →" variant="secondary"
                        title="Open the Mentions feed filtered to these posts"
                        onClick={() => { onViewInMentions(target); onClose(); }} />
                </div>
            </div>
        </CommonModal>
    );
}
